/**
 * intro_video.ts — the INTRO's pure decisions (2026-09-07: "after you
 * selected your avatar and you go to the loading screen … an overlay of
 * videos/intro.mp4 … till the end unless the user skips it by clicking";
 * then: "have the video play out in full even when the world is loaded, you
 * can only skip the video when the user clicks on the video").
 *
 * 2026-09-09 — THE NARRATION CARD before the film, and the world WAITS:
 * "have that image present initially fade in, then start intro_audio.mp3
 * (the narration) and intro_music.mp3 start at the same time, have the music
 * slightly quieter than the narration and show the image in the background
 * till the music audio stops, and then immediately start the intro video …
 * do not start loading in the world till the end of the video or when the
 * user decides to skip the video … have the narration skippable also and it
 * just jumps to the intro video". Two stages in one overlay, ONE forward of
 * atlas:menu-enter at the end (lib/journey.ts).
 *
 * Pure so the jsdom harness pins every branch; the module only wires DOM.
 */
export const INTRO_VIDEO_SRC = 'videos/intro.mp4';
/** The narration card's picture (2496×1664 PNG, 7.4 MB — decoded BEFORE the fade). */
export const INTRO_IMAGE_SRC = 'intro/intro-img.png';
/** The narration (66.6 s). */
export const INTRO_NARRATION_SRC = 'intro/intro-audio.mp3';
/** The music bed (79.5 s) — the card holds until THIS ends. */
export const INTRO_MUSIC_SRC = 'intro/intro_music.mp3';
/** Track gains: the music "slightly quieter than the narration". */
export const INTRO_NARRATION_GAIN = 1;
export const INTRO_MUSIC_GAIN = 0.5;
/** The picture's fade-in from black (ms); the tracks start when it completes. */
export const INTRO_CARD_FADE_MS = 1500;
/** The card's slow ZOOM-OUT (2026-09-09, "zoomed up fairly close … slowly zoom
 *  out and once the music is done the full image is filling up the whole
 *  screen"): the picture sits at this scale through the fade-in and pulls
 *  back linearly to 1 — the full picture, cover-fit — over the music's length. */
export const INTRO_CARD_ZOOM_FROM = 1.4;
/** The shipped music's length (s): the zoom's duration when the element has no metadata yet. */
export const INTRO_MUSIC_S = 79.464;
/** The PNG's decode is awaited before the fade, but never longer than this (ms). */
export const INTRO_DECODE_MAX_MS = 8000;
/** A skipped card ramps both tracks out over this (ms) — no pop. */
export const INTRO_AUDIO_RAMP_MS = 120;
/** Failsafe: the card never outlives this many seconds (the music is 79.5 s). */
export const INTRO_CARD_MAX_S = 120;
/** A click that follows a pointerdown inside this window is the SAME press (one gesture, one skip). */
export const INTRO_GESTURE_MS = 600;
/** Overlay fade-out (ms) on the film's exit. */
export const INTRO_FADE_MS = 350;
/** Failsafe: the film never outlives this many seconds of wall time. */
export const INTRO_MAX_S = 120;
/** localStorage key: 'off' never plays, 'on' plays even under automation. */
export const INTRO_PREF_KEY = 'atlas:intro-video';

export type IntroSkipReason = 'no-load-screen' | 'opt-out' | 'webdriver' | 'resumed' | 'already-played';
export type IntroEndReason = 'ended' | 'skipped' | 'error' | 'blocked' | 'timeout';
/** The overlay's two stages: the narration CARD, then the FILM. */
export type IntroStage = 'card' | 'video';
/** How atlas:menu-enter was forwarded: at once (no intro), or after the intro. */
export type IntroHandoff = 'immediate' | 'after-intro';

export interface IntroInputs {
  /** The boot load screen exists (harness / embedded pages ship none). */
  hasLoadScreen: boolean;
  /** localStorage 'atlas:intro-video' ('on' | 'off' | null). */
  pref: string | null;
  /** `?intro=off` on the URL. */
  query: string | null;
  /** navigator.webdriver — puppeteer / automation: the 137 MB decode would starve a swiftshader probe. */
  webdriver: boolean;
  /** This page load is a RECONNECT resume (session_resume): no menu, no traveler, no intro. */
  resumed: boolean;
  /** Once per page load. */
  played: boolean;
}

export function introDecision(i: IntroInputs): { play: true } | { play: false; why: IntroSkipReason } {
  if (!i.hasLoadScreen) return { play: false, why: 'no-load-screen' };
  if (i.played) return { play: false, why: 'already-played' };
  if (i.pref === 'off' || i.query === 'off') return { play: false, why: 'opt-out' };
  if (i.resumed) return { play: false, why: 'resumed' };
  if (i.webdriver && i.pref !== 'on') return { play: false, why: 'webdriver' };
  return { play: true };
}

/** The CARD's exits (every one leads to the film): both tracks broken →
 *  error; a click → skipped (returning players need not listen again); the
 *  MUSIC's end → ended ("show the image … till the music audio stops"); a
 *  broken music file makes the narration the clock; a failsafe clock. */
export function cardExit(e: {
  musicEnded: boolean; narrationEnded: boolean; musicError: boolean; narrationError: boolean;
  clicked: boolean; elapsedS: number;
}): IntroEndReason | null {
  if (e.musicError && e.narrationError) return 'error';
  if (e.clicked) return 'skipped';
  if (e.musicEnded) return 'ended';
  if (e.musicError && e.narrationEnded) return 'ended';
  if (e.elapsedS >= INTRO_CARD_MAX_S) return 'timeout';
  return null;
}

/** The FILM's exits: its own end, or a CLICK on it. The world finishing
 *  its load is NOT an exit (it no longer even starts under the film); a
 *  broken file and a failsafe clock are the only other ways down. */
export function introExit(e: { ended: boolean; clicked: boolean; error: boolean; elapsedS: number }): IntroEndReason | null {
  if (e.error) return 'error';
  if (e.clicked) return 'skipped';
  if (e.ended) return 'ended';
  if (e.elapsedS >= INTRO_MAX_S) return 'timeout';
  return null;
}

/** The zoom-out plan: from INTRO_CARD_ZOOM_FROM to 1 over the music's length
 *  (its metadata when known, else the shipped length), so the picture fills
 *  the screen exactly as the music ends; reduced motion holds the full picture. */
export function cardZoomPlan(musicDurationS: number | null | undefined, reducedMotion = false): { from: number; to: 1; ms: number } {
  const s = musicDurationS != null && Number.isFinite(musicDurationS) && musicDurationS > 0 ? musicDurationS : INTRO_MUSIC_S;
  return { from: reducedMotion ? 1 : INTRO_CARD_ZOOM_FROM, to: 1, ms: Math.round(s * 1000) };
}

/** The hint under each stage: the card's click skips the NARRATION (to the film), the film's click skips the film. */
export function stageHint(stage: IntroStage): string {
  return stage === 'card' ? 'click to skip narration' : 'click to skip';
}
